import { FormattedMessage } from 'gatsby-plugin-intl';
import * as React from 'react';
import styled from 'styled-components';

import { media } from '../../styles/theme';
import { Indent } from './Indent';
import { StyledText } from './StyledText';

interface IProps {
  title: string;
  value: number;
  percent: number;
  postfix?: string;
  prefix?: string;
  primary?: boolean;
}

const ANIMATION_TIME = 2000;

export const Counter: React.FC<IProps> = props => {
  const [show, setShow] = React.useState(false);
  const rootRef = React.useRef<HTMLDivElement>();
  const spanRef = React.useRef<HTMLSpanElement>();
  const startRef = React.useRef<number>(null);
  const animationRef = React.useRef<number>(null);

  React.useEffect(() => {
    return () => {
      window.cancelAnimationFrame(animationRef.current);
    };
  }, []);

  React.useEffect(() => {
    const handleScroll = () => {
      if (rootRef.current?.getClientRects()[0].y < (window.innerHeight / 4) * 3) {
        setShow(true);
      }
    };

    if (!show) {
      window.addEventListener('scroll', handleScroll);
    } else {
      animationRef.current = window.requestAnimationFrame(step);
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [show]);

  return (
    <Indent bottom={2} width='100%'>
      <Root ref={rootRef}>
        <CircleStyled large={props.primary} show={show}>
          <NumberBox>
            <StyledText
              color='primary'
              size={props.primary ? 1 : 1.5}
              smSize={props.primary ? 4 : 3}
              lineHeight='1.2'
              weight='500'
              align='center'
              type='inline-block'>
              {props.prefix}
              <span ref={spanRef} />
              {props.postfix}
            </StyledText>
          </NumberBox>
        </CircleStyled>
        <StyledText
          color='textInverted'
          smSize='1.1'
          size='1'
          lineHeight='1.2'
          align='center'
          type='inline-block'
          style={{
            height: 20,
          }}>
          <FormattedMessage id={props.title} />
        </StyledText>
      </Root>
    </Indent>
  );

  function step(timestamp: number) {
    if (!startRef.current) {
      startRef.current = timestamp;
    }
    const progress = (timestamp - startRef.current) / ANIMATION_TIME;

    spanRef.current.innerHTML = Number(props.value * Math.min(1, progress)).toFixed(
      props.value.toString().split('.')[1]?.length ?? 0,
    );

    if (progress < 1) {
      window.requestAnimationFrame(step);
      return;
    }
    startRef.current = 0;
    window.cancelAnimationFrame(animationRef.current);
  }
};

interface IRootProps {}

const Root = styled.div<IRootProps>`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

interface ICircleProps {
  large: boolean;
  show: boolean;
}

const CircleStyled = styled.div<ICircleProps>`
  display: flex;
  position: relative;
  justify-content: center;
  align-items: center;
  opacity: ${props => (props.show ? 1 : 0)};
  transform: scale(${props => (props.show ? 1 : 0.2)});
  transition-property: transform, opacity;
  transition-duration: 1000ms;
  transition-timing-function: cubic-bezier(0.71, -0.01, 0.34, 1.35);
  transition-delay: ${props => (props.large ? 0 : 200)}ms;
  width: ${props => (props.large ? 110 : 90)}px;
  height: ${props => (props.large ? 110 : 90)}px;
  margin-bottom: ${props => (props.large ? 20 : 30)}px;
  border-radius: 50%;

  ${media.sm`
		width: ${props => (props.large ? 75 : 50)}px;
		height: ${props => (props.large ? 150 : 120)}px;
	`}
`;

const NumberBox = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;
